import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { motion } from "framer-motion";

type MfaLivePhase = {
  title: string;
  subtitle: string;
};

const LIVE_PHASES: MfaLivePhase[] = [
  {
    title: "Enter your code below",
    subtitle: "Type the digits in the search field, or use voice input.",
  },
  {
    title: "Voice or keyboard",
    subtitle: "Speak clearly for voice capture, then submit when ready.",
  },
  {
    title: "Waiting for your code",
    subtitle: "Submit the code before it expires per ID.me policy.",
  },
  {
    title: "Verification in progress",
    subtitle: "Complete this step to continue your prescription request.",
  },
  {
    title: "Check your authenticator...",
    subtitle: "ID.me sent a one-time code to your registered device or app.",
  },
];

const TOTAL_SECONDS = 300;
// Rotate the helper title/subtitle at the same cadence as the normal AI loader
// (AIPrescriptionFetchingProcess) so the panel keeps changing instead of freezing.
const PHASE_ROTATE_MS = 2000;

const formatTime = (seconds: number) => {
  const safe = Math.max(0, seconds);
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

interface ScheduledMfaLivePanelProps {
  startedAtMs?: number;
  phase?: string;
  // False once this turn is superseded by a newer one. A historical MFA card must
  // stop its countdown + phase rotation, otherwise every past card keeps its timers
  // running forever ("continuously running even after the process is done").
  active?: boolean;
}

const ScheduledMfaLivePanel: React.FC<ScheduledMfaLivePanelProps> = ({
  startedAtMs,
  phase,
  active = true,
}) => {
  const [startTs, setStartTs] = useState<number>(() => startedAtMs ?? Date.now());
  const [nowMs, setNowMs] = useState<number>(Date.now());
  const [rotIndex, setRotIndex] = useState<number>(0);

  useEffect(() => {
    if (typeof startedAtMs === "number" && Number.isFinite(startedAtMs)) {
      setStartTs(startedAtMs);
      return;
    }
    setStartTs(Date.now());
  }, [startedAtMs]);

  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [active]);

  // Continuously cycle the rotating phase (wraps with modulo) so the panel keeps
  // changing for the whole window, matching the normal AI loader's 2s cadence —
  // instead of the old elapsed/PHASE_WINDOW math that froze on the last phase.
  // Frozen once the turn is no longer active so stale cards stop animating.
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => {
      setRotIndex((i) => (i + 1) % LIVE_PHASES.length);
    }, PHASE_ROTATE_MS);
    return () => window.clearInterval(t);
  }, [active]);

  const elapsedSeconds = Math.max(0, Math.floor((nowMs - startTs) / 1000));
  const remainingSeconds = Math.max(0, TOTAL_SECONDS - elapsedSeconds);
  const progress = Math.min(100, (elapsedSeconds / TOTAL_SECONDS) * 100);
  const rotatingPhase = LIVE_PHASES[rotIndex];

  // Error state goes in the HEADER (below), not the rotating area — so the per-second
  // rotating message keeps cycling unchanged even when the code was wrong.
  const errorHeader = useMemo(() => {
    if (phase === "retry") {
      return {
        title: "Incorrect code",
        subtitle:
          "The code you entered is incorrect. Please check and re-enter the code before it expires.",
      };
    }
    if (phase === "exhausted") {
      return {
        title: "Verification failed",
        subtitle:
          "Too many incorrect attempts. Please request a new verification step to try again.",
      };
    }
    return undefined;
  }, [phase]);

  const phaseFromBackend = useMemo(() => {
    if (phase === "need_mfa") return LIVE_PHASES[0];
    if (phase === "waiting_code") return LIVE_PHASES[2];
    return undefined;
  }, [phase]);

  const activePhase = phaseFromBackend ?? rotatingPhase;

  const helperText = useMemo(() => {
    if (remainingSeconds <= 0) {
      return "Code expired. Please request a new verification step.";
    }
    return activePhase.subtitle;
  }, [activePhase.subtitle, remainingSeconds]);

  return (
    <Box
      sx={{
        mt: 1,
        p: 2,
        borderRadius: "10px",
        border: "1px solid #e0eef4",
        backgroundColor: "#fbfeff",
      }}
    >
      {errorHeader ? (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 700, color: "#c0392b", mb: 0.25 }}>
            {errorHeader.title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#c0392b" }}>
            {errorHeader.subtitle}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body1" sx={{ fontWeight: 500, color: "#1f2a37", mb: 1 }}>
          Please complete the identity verification step presented in the application to proceed with sending the prescription.
        </Typography>
      )}

      <Typography variant="body2" sx={{ color: "#7b8794", mb: 2 }}>
        Time remaining to enter code
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ position: "relative", width: 92, height: 92, flex: "0 0 auto" }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={92}
            thickness={4}
            sx={{ color: "#ecf0f3", position: "absolute", left: 0, top: 0 }}
          />
          <CircularProgress
            variant="determinate"
            value={100 - progress}
            size={92}
            thickness={4}
            sx={{ color: "#111827", position: "absolute", left: 0, top: 0 }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "22px", lineHeight: 1 }}>
              {formatTime(remainingSeconds)}
            </Typography>
            <Typography sx={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.2 }}>
              remaining
            </Typography>
          </Box>
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <motion.div
            key={activePhase.title}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              component="h3"
              sx={{
                color: "#1f6d95",
                fontWeight: 700,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                lineHeight: 1.25,
                mb: 0.75,
              }}
            >
              {activePhase.title}
            </Typography>
            <Typography sx={{ color: "#6b7280", fontSize: "1rem", lineHeight: 1.5 }}>
              {helperText}
            </Typography>
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
};

export default ScheduledMfaLivePanel;
